import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface QaRun {
  runId: string;
  timestamp: string;
  mode: string;
  scenarioCount: number;
  passCount: number;
  failCount: number;
  warnCount: number;
}

interface DiffEntry {
  field: string;
  expected: unknown;
  actual: unknown;
  severity: 'critical' | 'warning' | 'info';
  message: string;
}

interface ScenarioResult {
  scenarioId: string;
  label: string;
  passed: boolean;
  diffs: DiffEntry[];
}

interface BudgetStats {
  rpcCalls: { used: number; max: number };
  apiCalls: { used: number; max: number };
  cacheHitRate: number;
  exceeded: boolean;
}

interface ProviderUsageStats {
  totalLatencyMs: number;
  avgLatencyMs: number;
  cacheHits: number;
  cacheMisses: number;
}

interface QaReport {
  timestamp: string;
  mode: string;
  scenarioCount: number;
  passCount: number;
  failCount: number;
  warnCount: number;
  results: ScenarioResult[];
  budget?: BudgetStats;
  providerStats?: ProviderUsageStats;
}

interface GoldenScenario {
  scenarioId: string;
  riskLevel: string;
  normalizedScore: number;
  confidence: number;
  findingsCount: number;
  recommendationsCount: number;
  riskFactors: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'ceo_qa_token';
const REFRESH_MS = 60_000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(iso: string): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 1000) return 'Just now';
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function runStatusChip(run: QaRun): { label: string; cls: string } {
  if (run.failCount > 0) return { label: 'REGRESSION', cls: 'qa-chip qa-chip-fail' };
  if (run.warnCount > 0) return { label: 'DRIFT', cls: 'qa-chip qa-chip-warn' };
  return { label: 'CLEAN', cls: 'qa-chip qa-chip-pass' };
}

function scenarioStatusChip(r: ScenarioResult): { label: string; cls: string } {
  if (!r.passed) {
    const hasCritical = r.diffs.some((d) => d.severity === 'critical');
    if (hasCritical) return { label: 'FAIL', cls: 'qa-chip qa-chip-fail' };
    return { label: 'WARNING', cls: 'qa-chip qa-chip-warn' };
  }
  return { label: 'PASS', cls: 'qa-chip qa-chip-pass' };
}

function riskChipCls(level: string): string {
  switch (level) {
    case 'GREEN': return 'qa-chip qa-chip-green';
    case 'YELLOW': return 'qa-chip qa-chip-yellow';
    case 'RED': return 'qa-chip qa-chip-red';
    case 'UNKNOWN': return 'qa-chip qa-chip-muted';
    default: return 'qa-chip';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QaDashboard() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? '');
  const [tokenInput, setTokenInput] = useState('');
  const [runs, setRuns] = useState<QaRun[]>([]);
  const [goldens, setGoldens] = useState<GoldenScenario[]>([]);
  const [selectedReport, setSelectedReport] = useState<QaReport | null>(null);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch runs list
  const fetchRuns = useCallback(async (t: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/ceo/qa/runs', {
        headers: { 'x-ceo-token': t },
      });
      if (!res.ok) {
        if (res.status === 403) {
          setError('Invalid CEO token');
          sessionStorage.removeItem(TOKEN_KEY);
          setToken('');
        } else {
          setError(`API error: ${res.status}`);
        }
        return;
      }
      const json = await res.json();
      setRuns(json.runs || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch goldens (once)
  const fetchGoldens = useCallback(async (t: string) => {
    try {
      const res = await fetch('/api/admin/ceo/qa/goldens', {
        headers: { 'x-ceo-token': t },
      });
      if (res.ok) {
        const json = await res.json();
        setGoldens(json.scenarios || []);
      }
    } catch {
      // Non-critical
    }
  }, []);

  // Fetch a specific run detail
  const fetchRunDetail = useCallback(async (t: string, runId: string) => {
    try {
      const res = await fetch(`/api/admin/ceo/qa/runs/${runId}`, {
        headers: { 'x-ceo-token': t },
      });
      if (res.ok) {
        const json = await res.json();
        setSelectedReport(json.report);
        setSelectedRunId(runId);
      }
    } catch {
      setError('Failed to load run detail');
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    sessionStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
  };

  // Initial fetch + auto-refresh
  useEffect(() => {
    if (!token) return;
    fetchRuns(token);
    fetchGoldens(token);
    const id = setInterval(() => fetchRuns(token), REFRESH_MS);
    return () => clearInterval(id);
  }, [token, fetchRuns, fetchGoldens]);

  // Login screen
  if (!token) {
    return (
      <div className="qa-page">
        <div className="qa-login-card">
          <h1>QA Report Dashboard</h1>
          <p className="qa-login-desc">Enter your CEO dashboard token to view QA reports.</p>
          <form onSubmit={handleLogin} className="qa-login-form">
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="CEO Dashboard Token"
              className="qa-login-input"
              autoFocus
            />
            <button type="submit" className="btn btn-primary">
              Authenticate
            </button>
          </form>
          {error && <p className="qa-error">{error}</p>}
        </div>
      </div>
    );
  }

  // Compute distribution from selected report
  const distribution = selectedReport
    ? (() => {
        const goldenMap = new Map(goldens.map((g) => [g.scenarioId, g]));
        let green = 0, yellow = 0, red = 0;
        for (const r of selectedReport.results) {
          const g = goldenMap.get(r.scenarioId);
          const level = g?.riskLevel ?? 'GREEN';
          if (level === 'GREEN') green++;
          else if (level === 'YELLOW') yellow++;
          else red++;
        }
        return { green, yellow, red };
      })()
    : null;

  return (
    <div className="qa-page">
      <div className="qa-header">
        <h1>QA Report Dashboard</h1>
        {error && <p className="qa-error">{error}</p>}
        {loading && runs.length === 0 && <p className="qa-loading">Loading...</p>}
      </div>

      {/* A) Latest QA Runs Table */}
      <section className="qa-section">
        <h2>Latest QA Runs</h2>
        {runs.length === 0 && !loading ? (
          <p className="qa-empty">No QA runs found. Run <code>yarn qa:run</code> to generate reports.</p>
        ) : (
          <table className="qa-runs-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Mode</th>
                <th>Pass</th>
                <th>Warn</th>
                <th>Fail</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const chip = runStatusChip(run);
                const isSelected = selectedRunId === run.runId;
                return (
                  <tr
                    key={run.runId}
                    className={`qa-runs-row ${isSelected ? 'qa-runs-row-selected' : ''}`}
                    onClick={() => fetchRunDetail(token, run.runId)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td title={run.timestamp}>{relativeTime(run.timestamp)}</td>
                    <td>
                      {run.mode === 'real' ? (
                        <span className="qa-chip qa-chip-real">REAL MODE</span>
                      ) : (
                        run.mode
                      )}
                    </td>
                    <td>{run.passCount}</td>
                    <td>{run.warnCount}</td>
                    <td>{run.failCount}</td>
                    <td>{run.scenarioCount}</td>
                    <td><span className={chip.cls}>{chip.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* B) Run Detail Panel */}
      {selectedReport && (
        <section className="qa-section qa-detail-panel">
          <h2>Run Detail — {relativeTime(selectedReport.timestamp)}</h2>

          {/* Distribution bar */}
          {distribution && (
            <div className="qa-distribution-section">
              <h3>Scoring Distribution</h3>
              <div className="qa-distribution">
                {distribution.green > 0 && (
                  <div
                    className="qa-dist-segment qa-dist-green"
                    style={{ flex: distribution.green }}
                  >
                    {distribution.green} GREEN
                  </div>
                )}
                {distribution.yellow > 0 && (
                  <div
                    className="qa-dist-segment qa-dist-yellow"
                    style={{ flex: distribution.yellow }}
                  >
                    {distribution.yellow} YELLOW
                  </div>
                )}
                {distribution.red > 0 && (
                  <div
                    className="qa-dist-segment qa-dist-red"
                    style={{ flex: distribution.red }}
                  >
                    {distribution.red} RED
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Budget & Provider Stats (real-mode only) */}
          {selectedReport.budget && (
            <div className="qa-budget-section">
              <h3>Budget Usage {selectedReport.budget.exceeded && <span className="qa-chip qa-chip-fail">EXCEEDED</span>}</h3>
              <div className="qa-metrics-row">
                <div className="qa-metric-card">
                  <div className="qa-metric-label">RPC Calls</div>
                  <div className="qa-metric-value">{selectedReport.budget.rpcCalls.used}<span className="qa-metric-sub"> / {selectedReport.budget.rpcCalls.max}</span></div>
                </div>
                <div className="qa-metric-card">
                  <div className="qa-metric-label">API Calls</div>
                  <div className="qa-metric-value">{selectedReport.budget.apiCalls.used}<span className="qa-metric-sub"> / {selectedReport.budget.apiCalls.max}</span></div>
                </div>
                <div className="qa-metric-card">
                  <div className="qa-metric-label">Cache Hit Rate</div>
                  <div className="qa-metric-value">{(selectedReport.budget.cacheHitRate * 100).toFixed(1)}%</div>
                </div>
                {selectedReport.providerStats && (
                  <div className="qa-metric-card">
                    <div className="qa-metric-label">Avg Latency</div>
                    <div className="qa-metric-value">{selectedReport.providerStats.avgLatencyMs}<span className="qa-metric-sub">ms</span></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Scenario list */}
          <h3>Scenarios</h3>
          <table className="qa-runs-table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Risk</th>
                <th>Score</th>
                <th>Confidence</th>
                <th>Findings</th>
                <th>Diffs</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {selectedReport.results.map((r) => {
                const chip = scenarioStatusChip(r);
                const golden = goldens.find((g) => g.scenarioId === r.scenarioId);
                const riskLevel = golden?.riskLevel ?? '--';
                const score = golden?.normalizedScore ?? '--';
                const conf = golden?.confidence ?? '--';
                const findings = golden?.findingsCount ?? '--';
                const criticalCount = r.diffs.filter((d) => d.severity === 'critical').length;
                const warnCount = r.diffs.filter((d) => d.severity === 'warning').length;
                const diffSummary = r.diffs.length === 0
                  ? '--'
                  : `${criticalCount}C / ${warnCount}W / ${r.diffs.length - criticalCount - warnCount}I`;

                return (
                  <tr
                    key={r.scenarioId}
                    className="qa-scenario-row"
                    onClick={() =>
                      navigate(`/admin/qa/scenario/${r.scenarioId}?runId=${selectedRunId}`)
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span className="qa-scenario-id">{r.scenarioId}</span>
                      <br />
                      <span className="qa-scenario-label">{r.label}</span>
                    </td>
                    <td><span className={riskChipCls(riskLevel)}>{riskLevel}</span></td>
                    <td>{score}</td>
                    <td>{conf}</td>
                    <td>{findings}</td>
                    <td className="qa-mono">{diffSummary}</td>
                    <td><span className={chip.cls}>{chip.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* C) Golden Scenarios Summary */}
      {goldens.length > 0 && (
        <section className="qa-section">
          <h2>Golden Scenarios</h2>
          <table className="qa-runs-table qa-golden-table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Risk Level</th>
                <th>Score</th>
                <th>Confidence</th>
                <th>Findings</th>
              </tr>
            </thead>
            <tbody>
              {goldens.map((g) => (
                <tr key={g.scenarioId}>
                  <td>{g.scenarioId}</td>
                  <td><span className={riskChipCls(g.riskLevel)}>{g.riskLevel}</span></td>
                  <td>{g.normalizedScore}</td>
                  <td>{g.confidence}</td>
                  <td>{g.findingsCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <p className="qa-footer">
        Auto-refreshes every 60s &middot; Reads local reports only &middot; No provider calls
      </p>
    </div>
  );
}
